import { Helmet } from 'react-helmet-async';

export const StructuredData = ({ type = 'default', data = {} }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://savea.info';

  // Données de l'organisation (toujours présentes)
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SAVEA",
    "legalName": "SOLUCIONES INTEGRALES HORIZON, S.L.U.",
    "url": baseUrl,
    "logo": `${baseUrl}/logo.png`,
    "description": "Plateforme de comparaison et d'optimisation des dépenses domestiques en France et Espagne. Comparez les fournisseurs d'énergie, internet, et accédez à des outils gratuits pour réduire vos factures.",
    "foundingDate": "2024",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Calle Radas 57, Ático 1",
      "addressLocality": "Barcelona",
      "postalCode": "08004",
      "addressCountry": "ES"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+34-900-000-000",
      "contactType": "customer service",
      "availableLanguage": ["French", "Spanish"]
    },
    "sameAs": [
      baseUrl
    ]
  };

  // Données du site web
  const websiteData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SAVEA",
    "url": baseUrl,
    "description": "Comparez les fournisseurs d'énergie et internet, économisez sur vos factures avec nos outils gratuits et guides pratiques.",
    "inLanguage": ["fr-FR", "es-ES"],
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/fr/comparateur-electricite?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  // Données pour page d'accueil
  const homePageData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "SAVEA - Reprenez le contrôle de votre pouvoir d'achat",
    "description": "Plateforme gratuite pour comparer les fournisseurs d'énergie et internet, réduire vos factures et optimiser votre budget familial.",
    "url": baseUrl,
    "mainEntity": {
      "@type": "Service",
      "name": "Comparateur de Fournisseurs",
      "description": "Service gratuit de comparaison des fournisseurs d'électricité, gaz et internet en France et Espagne",
      "provider": {
        "@type": "Organization",
        "name": "SAVEA"
      },
      "serviceType": "Comparaison de fournisseurs",
      "areaServed": ["FR", "ES"],
      "audience": {
        "@type": "Audience",
        "audienceType": "Households"
      }
    }
  };

  // Données pour comparateur d'électricité
  const electricityComparatorData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Comparateur d'Électricité SAVEA",
    "description": "Comparez gratuitement les offres d'électricité des principaux fournisseurs en France. Trouvez l'offre la moins chère adaptée à votre consommation.",
    "url": `${baseUrl}/fr/comparateur-electricite`,
    "applicationCategory": "UtilitiesApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "featureList": [
      "Comparaison de +20 fournisseurs d'électricité",
      "Calcul personnalisé selon votre consommation",
      "Offres vertes et tarifs réglementés",
      "Service 100% gratuit et sans engagement"
    ]
  };

  // Données pour le guide anti-inflation
  const guideData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Guide Anti-Inflation 2026 - Comment économiser 1500-3000€/an",
    "description": "Guide complet et gratuit pour réduire vos dépenses sans vous priver. 10 chapitres avec stratégies éprouvées pour combattre l'inflation.",
    "author": {
      "@type": "Organization",
      "name": "SAVEA"
    },
    "publisher": {
      "@type": "Organization",
      "name": "SAVEA",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`
      }
    },
    "datePublished": "2026-01-01",
    "dateModified": "2026-01-01",
    "articleBody": "Guide pratique complet pour optimiser vos dépenses dans tous les domaines : énergie, alimentation, transport, logement, assurances, abonnements, etc."
  };

  // Fil d'Ariane
  const breadcrumbData = data.breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": data.breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${baseUrl}${item.url}`
    }))
  } : null;

  // FAQ pour pages fournisseurs
  const faqData = data.faqs ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  // Sélection des données selon le type de page
  let structuredDataArray = [organizationData, websiteData];

  switch (type) {
    case 'home':
      structuredDataArray.push(homePageData);
      break;
    case 'electricity-comparator':
      structuredDataArray.push(electricityComparatorData);
      break;
    case 'guide':
      structuredDataArray.push(guideData);
      break;
    default:
      break;
  }

  if (breadcrumbData) structuredDataArray.push(breadcrumbData);
  if (faqData) structuredDataArray.push(faqData);

  return (
    <Helmet>
      {structuredDataArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};
